"use client";

import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";
import { BottomSheetDrawer } from "@/components/bottom-sheet-drawer";
import { Button } from "@/components/ui/button";
import { DietsListSortForm } from "@/features/diets/components/diets-list-sort-form";
import { cn } from "@/lib/utils";

type ListingMobileFiltersProps = {
  mobileFilters?: React.ReactNode;
  sorting?: boolean;
};

const mobileListingBreakpoint = 1024;
const scrollDirectionThreshold = 56;

export const ListingMobileFilters = ({
  mobileFilters,
  sorting = true,
}: ListingMobileFiltersProps) => {
  const t = useTranslations();
  const containerRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLElement | null>(null);
  const isAttachedToHeaderRef = useRef(false);
  const isHeaderVisibleRef = useRef(false);
  const scrollDistanceRef = useRef(0);
  const scrollDirectionRef = useRef<"up" | "down">("down");
  const lastScrollYRef = useRef(0);
  const frameRef = useRef<number | null>(null);
  const [barHeight, setBarHeight] = useState(0);
  const [isFixed, setIsFixed] = useState(false);
  const [fixedTop, setFixedTop] = useState(0);
  const [isSortingDrawerOpen, setIsSortingDrawerOpen] = useState(false);

  useEffect(() => {
    const setHeaderTop = (nextTop: string) => {
      const rootElement = document.documentElement;
      const currentTop = rootElement.style.getPropertyValue(
        "--listing-mobile-header-top"
      );

      if (currentTop === nextTop) {
        return;
      }

      rootElement.style.setProperty("--listing-mobile-header-top", nextTop);
    };

    const setNextBarHeight = (nextHeight: number) => {
      setBarHeight((currentHeight) =>
        currentHeight === nextHeight ? currentHeight : nextHeight
      );
    };

    const setFixedState = (nextIsFixed: boolean, nextTop: number) => {
      setIsFixed((currentValue) =>
        currentValue === nextIsFixed ? currentValue : nextIsFixed
      );
      setFixedTop((currentTop) => (currentTop === nextTop ? currentTop : nextTop));
    };

    const setAttachedToHeader = (isAttached: boolean) => {
      if (isAttachedToHeaderRef.current === isAttached) {
        return;
      }

      isAttachedToHeaderRef.current = isAttached;
      window.dispatchEvent(
        new CustomEvent("listing-mobile-filters-attach-change", {
          detail: { isAttached },
        })
      );
    };

    const applyState = ({
      headerHeight,
      isFixedBar,
      isHeaderVisible,
    }: {
      headerHeight: number;
      isFixedBar: boolean;
      isHeaderVisible: boolean;
    }) => {
      if (!isFixedBar) {
        setHeaderTop("0px");
        setAttachedToHeader(false);
        setFixedState(false, 0);
        return;
      }

      if (isHeaderVisible) {
        setHeaderTop("0px");
        setAttachedToHeader(true);
        setFixedState(true, headerHeight);
        return;
      }

      setHeaderTop(`-${headerHeight}px`);
      setAttachedToHeader(false);
      setFixedState(true, 0);
    };

    const syncStickyState = () => {
      headerRef.current = document.querySelector<HTMLElement>(
        '[data-sticky-main-header="true"]'
      );

      if (!headerRef.current || !containerRef.current || !sentinelRef.current) {
        return;
      }

      const isMobileListingViewport = window.innerWidth < mobileListingBreakpoint;
      const headerHeight = Math.round(
        headerRef.current.getBoundingClientRect().height
      );
      const currentScrollY = window.scrollY;
      const nextBarHeight = Math.round(
        containerRef.current.getBoundingClientRect().height
      );

      setNextBarHeight(nextBarHeight);

      if (!isMobileListingViewport) {
        applyState({
          headerHeight,
          isFixedBar: false,
          isHeaderVisible: false,
        });
        scrollDistanceRef.current = 0;
        isHeaderVisibleRef.current = false;
        scrollDirectionRef.current = "down";
        lastScrollYRef.current = currentScrollY;
        return;
      }

      const hasReachedStickyStart =
        sentinelRef.current.getBoundingClientRect().top <= 0;

      if (!hasReachedStickyStart) {
        applyState({
          headerHeight,
          isFixedBar: false,
          isHeaderVisible: false,
        });
        scrollDistanceRef.current = 0;
        isHeaderVisibleRef.current = false;
        scrollDirectionRef.current = "down";
        lastScrollYRef.current = currentScrollY;
        return;
      }

      const scrollDelta = currentScrollY - lastScrollYRef.current;

      if (scrollDelta === 0) {
        applyState({
          headerHeight,
          isFixedBar: true,
          isHeaderVisible: isHeaderVisibleRef.current,
        });
        return;
      }

      const nextDirection = scrollDelta > 0 ? "down" : "up";

      if (scrollDirectionRef.current !== nextDirection) {
        scrollDirectionRef.current = nextDirection;
        scrollDistanceRef.current = Math.abs(scrollDelta);
      } else {
        scrollDistanceRef.current += Math.abs(scrollDelta);
      }

      if (
        scrollDirectionRef.current === "up" &&
        !isHeaderVisibleRef.current &&
        scrollDistanceRef.current >= scrollDirectionThreshold
      ) {
        isHeaderVisibleRef.current = true;
        scrollDistanceRef.current = 0;
      }

      if (
        scrollDirectionRef.current === "down" &&
        isHeaderVisibleRef.current &&
        scrollDistanceRef.current >= scrollDirectionThreshold
      ) {
        isHeaderVisibleRef.current = false;
        scrollDistanceRef.current = 0;
      }

      lastScrollYRef.current = currentScrollY;
      applyState({
        headerHeight,
        isFixedBar: true,
        isHeaderVisible: isHeaderVisibleRef.current,
      });
    };

    const handleSyncStickyState = () => {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
      }

      frameRef.current = window.requestAnimationFrame(() => {
        syncStickyState();
        frameRef.current = null;
      });
    };

    lastScrollYRef.current = window.scrollY;
    syncStickyState();

    window.addEventListener("scroll", handleSyncStickyState, { passive: true });
    window.addEventListener("resize", handleSyncStickyState);

    return () => {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
      }
      window.removeEventListener("scroll", handleSyncStickyState);
      window.removeEventListener("resize", handleSyncStickyState);
      setAttachedToHeader(false);
      document.documentElement.style.removeProperty("--listing-mobile-header-top");
    };
  }, []);

  if (!sorting && !mobileFilters) {
    return null;
  }

  return (
    <div
      className="lg:hidden"
      ref={sentinelRef}
      style={{ height: isFixed ? barHeight : undefined }}
    >
      <div className="overflow-hidden w-full flex px-4 container">
        <div
          className={cn(
            "z-10 w-screen overflow-x-scroll bg-white py-3 no-scrollbar",
            isFixed && "fixed left-0 px-4 shadow-md"
          )}
          ref={containerRef}
          style={isFixed ? { top: fixedTop, transition: "top 0.3s ease"  } : undefined}
        >
          <div className="container flex gap-2 p-0">
            {sorting && (
              <BottomSheetDrawer
                className="z-99999999"
                onOpenChange={setIsSortingDrawerOpen}
                open={isSortingDrawerOpen}
                content={
                  <div className="p-4">
                    <DietsListSortForm
                      onSubmitted={() => setIsSortingDrawerOpen(false)}
                      submitOnChange
                    />
                  </div>
                }
                title={t("filters.sorting")}
                trigger={<Button variant="outline">{t("filters.sort")}</Button>}
              />
            )}
            {mobileFilters}
          </div>
        </div>
      </div>
    </div>
  );
};
